import { connect } from 'pwa-helpers';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import store from '../../store/store';
import '../product-card/product-card-display';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import '../order-history/order-history';
import { isHEInstructor } from '../../common/utils/cart-util';
import { getAnalyticsSafeData, isSegUser, isSuccessUser, selectUser } from '../user-profile/user-profile.selectors';
import '../pdp/card-buttons';
import environment, { isProdEnv } from '../../common/environment';
import { fetchUserProfile } from '../user-profile/user-profile.thunks';
import emit from '../../common/events';
import { UserProfile } from '../user-profile/user-profile.interface';
import { getAppFlow } from '../user-profile/utils/user-profile.util';

enum USER_TYPE {
  HE_STUDENT = 'HE_STUDENT',
  HE_INSTRUCTOR = 'HE_INSTRUCTOR',
  SEG_USER = 'SEG_USER',
}

export enum PRODUCT_TYPE {
  DIGITAL = 'DIGITAL',
  E_BOOK = 'E_BOOK',
  INCLUSIVE_ACCESS = 'INCLUSIVE_ACCESS',
}

const isProd = (env) => (isProdEnv() ? '' : env);

export const MY_ACCOUNT_ACCESS_REDIRECT_LINK = {
  [USER_TYPE.HE_STUDENT]: {
    [PRODUCT_TYPE.DIGITAL]: `https://shop${isProd(
      environment().env
    )}.mheducation.com/store/services/account/book/bookshelf.html`,
    [PRODUCT_TYPE.E_BOOK]: `https://myebooks${isProd('-' + environment().env)}.mheducation.com/bookshelf/ebooks`,
    [PRODUCT_TYPE.INCLUSIVE_ACCESS]: `https://${isProd(
      environment().env + '.'
    )}mheducation.com/account/inclusive-access-settings?flow=shop`,
  },
  [USER_TYPE.HE_INSTRUCTOR]: {
    [PRODUCT_TYPE.DIGITAL]: `https://shop${isProd(
      environment().env
    )}.mheducation.com/store/services/account/book/bookshelf.html`,
    [PRODUCT_TYPE.E_BOOK]: `https://myebooks${isProd('-' + environment().env)}.mheducation.com/bookshelf/ebooks`,
  },
};


export function getProductTypeRedirectLink(user: UserProfile, productType: PRODUCT_TYPE) {
  if (isHEInstructor(user)) {
    return MY_ACCOUNT_ACCESS_REDIRECT_LINK[USER_TYPE.HE_INSTRUCTOR]?.[productType];
  } else if (isSegUser(user)) {
    return MY_ACCOUNT_ACCESS_REDIRECT_LINK[USER_TYPE.SEG_USER]?.[productType];
  } else {
    return MY_ACCOUNT_ACCESS_REDIRECT_LINK[USER_TYPE.HE_STUDENT]?.[productType];
  }
}


@customElement('mhecomm-my-account-access-link')
export class MyAccountAccessLink extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .action-wrapper {
        background: #d1edf680;
      }

      .card a.stretched-link {
        outline: 0;
      }

      .card:focus-within {
        outline: 0.25rem solid var(--theme-red);
      }
    `,
  ];

  @state()
  private userType: USER_TYPE | undefined;

  @state()
  private isSelfOptOutEnabled = false;

  private user: UserProfile | undefined;

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile({checkSelfOptOut: true}));
  }

  stateChanged(state: StoreState) {
    this.isSelfOptOutEnabled = !!selectUser(state)?.isSelfOptOutEnabled;
    if (isSuccessUser(state)) {
      this.user = selectUser(state);
      if (isHEInstructor(this.user)) {
        this.userType = USER_TYPE.HE_INSTRUCTOR;
      } else if (isSegUser(this.user)) {
        this.userType = USER_TYPE.SEG_USER;
      } else {
        this.userType = USER_TYPE.HE_STUDENT;
      }
    } else {
      this.userType = undefined;
    }
  }

  renderComp() {
    if (!this.userType || this.userType === USER_TYPE.SEG_USER) return nothing;
  
    const products = [
      { type: PRODUCT_TYPE.DIGITAL, icon: 'display', label: 'My Digital Products', id: 'digital-products-link' , automationId: 'digital-products-link' },
      { type: PRODUCT_TYPE.E_BOOK, icon: 'tablet', label: 'My eBookshelf', id: 'ebookshelf-link'  , automationId: 'ebookshelf-link' },
      { type: PRODUCT_TYPE.INCLUSIVE_ACCESS, icon: 'mortarboard-group', label: 'Manage Inclusive Access', id: 'inclusive-access-link' , automationId: 'inclusive-access-link', condition: this.isSelfOptOutEnabled }
    ];
  
    return html`
      <div class="my-4 p-4 rounded-3 action-wrapper">
        <h5 class="mb-3">More account actions:</h5>
        <div class="row g-3 text-center">
          ${products.map(product => {
            const link = this.user ? getProductTypeRedirectLink(this.user, product.type) : '';
            if (link && (product.condition === undefined || product.condition)) {
              return html`
                <div class="col-12 col-md">
                  <div class="card py-4 rounded-3 shadow-sm fw-bold">
                    ${this.uxtIconReady ? html`<uxt-icon icon="${product.icon}" size="3xl" class="mb-3"></uxt-icon>` : nothing}
                    <a
                      data-automation-id=${product.automationId}
                      href="${link}"
                      class="stretched-link text-dark text-decoration-none"
                      @click=${(event: Event) => this.handleClick(event, product.id, product.label)}
                    >
                      ${product.label}
                    </a>
                  </div>
                </div>
              `;
            }
            return nothing;
          })}
        </div>
      </div>
    `;
  }

  handleClick(event: Event, id: string, label: string) {
    const link = event.currentTarget as HTMLAnchorElement;
    emit({
      element: this,
      name: 'click',
      detail: {
        id: id,
        label:label,
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        href: link.href
      },
      dataLayer: true
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-account-access-link': MyAccountAccessLink;
  }
}
